import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import hash from "hash-it";
import { BehaviorSubject, Observable } from "rxjs";
import { HttpActions } from "@webapp/core/abstracts/enums/http-actions.enum";
import { BaseCacheSettings } from "@webapp/core/abstracts/models/base-cache-settings.model";
import { BaseCache, BaseCacheItem } from "@webapp/core/abstracts/models/base-cache.model";
import { CacheIdentifier } from "@webapp/core/abstracts/models/cache-identifier.model";

@Injectable({
  providedIn: "any",
})
export abstract class BaseState {
  private loading$: BehaviorSubject<boolean>[] = [];
  private error$: BehaviorSubject<HttpErrorResponse | null>[] = [];
  private success$: BehaviorSubject<boolean>[] = [];

  private httpCache: BaseCache<unknown> = {};

  protected cacheSettings: BaseCacheSettings = { cacheExpiresInSeconds: 5, ignoreCache: false };

  constructor() {
    for (const httpActionsKey in HttpActions) {
      this.loading$[httpActionsKey] = new BehaviorSubject<boolean>(false);
      this.error$[httpActionsKey] = new BehaviorSubject<HttpErrorResponse | null>(null);
      this.success$[httpActionsKey] = new BehaviorSubject<boolean>(false);
    }
  }
  public isLoading$(method: HttpActions): Observable<boolean> {
    return this.loading$[method].asObservable();
  }

  public setIsLoading(method: HttpActions, isLoading: boolean): void {
    this.loading$[method].next(isLoading);
  }

  public isError$(method: HttpActions): Observable<HttpErrorResponse | null> {
    return this.error$[method].asObservable();
  }

  public setIsError(method: HttpActions, response: HttpErrorResponse | null): void {
    this.error$[method].next(response);
  }

  public isSuccess$(method: HttpActions): Observable<boolean> {
    return this.success$[method].asObservable();
  }

  public setIsSuccess(method: HttpActions, isSuccess: boolean): void {
    this.success$[method].next(isSuccess);
  }

  public resetState(): void {
    for (const httpActionsKey in HttpActions) {
      this.loading$[httpActionsKey].next(false);
      this.error$[httpActionsKey].next(null);
      this.success$[httpActionsKey].next(false);
    }
  }

  public getCacheSettings(): BaseCacheSettings {
    return this.cacheSettings;
  }

  public getCachedValue<ResponseType>(cacheIdentifier: CacheIdentifier): Observable<ResponseType> {
    const cacheHash = hash(cacheIdentifier);
    const item = this.httpCache[cacheHash];
    // no item provided
    if (!item) {
      return null;
    } else if (item.expires.getTime() < new Date().getTime()) {
      // item has expired, should not get in here at any pointsince the SetTimeout for cache clear should have cleared that record
      return null;
    } else {
      // return the cached value
      return this.httpCache[cacheHash].value as Observable<ResponseType>;
    }
  }

  public setCachedValue<ResponseType>(cacheIdentifier: CacheIdentifier, cacheObject: Observable<ResponseType>, expiresInSeconds = 5): void {
    const cacheHash = hash(cacheIdentifier);
    if (!this.httpCache[cacheHash]) {
      this.httpCache[cacheHash] = new BaseCacheItem<unknown>();
    }
    this.httpCache[cacheHash].value = cacheObject;
    this.httpCache[cacheHash].expires = new Date(new Date().getTime() + expiresInSeconds * 1000);

    // cleanup the cache
    setTimeout(() => {
      delete this.httpCache[cacheHash];
    }, expiresInSeconds * 1000);
  }
}
